import React, { useState, useEffect, useMemo } from 'react';
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Typography,
    IconButton, Alert, CircularProgress, Chip, InputAdornment,
} from '@mui/material';
import { Close, Search, CheckCircle, RadioButtonUnchecked, Inventory, ArrowBack } from '@mui/icons-material';
import { businessAPI } from '../api';
import { APP_DIALOG, APP_DIALOG_TITLE_SX } from '../utils/dialogStyle';
import { WEEKDAY_SHORT } from '../utils/schedule';

/**
 * Products another vendor shared: tick the ones you sell, check the prices, add them.
 * Copies are yours from then on — the sender's later changes never touch them.
 *
 * props: open, onClose, businessId, share (the notification), onAdded({ added, skipped })
 */
const unitOf = (p) => (p.f_billing_type === 'MonthlyFlatFee' ? 'per month' : 'per item');
const dayPricesText = (dp) => Object.entries(dp || {})
    .sort(([a], [b]) => a - b)
    .map(([d, v]) => `${WEEKDAY_SHORT[d]} ₹${v}`).join(', ');

const SharedProductsDialog = ({ open, onClose, businessId, share, onAdded }) => {
    const [step, setStep] = useState(0);              // 0 = choose, 1 = prices
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);           // { from_business_name, products }
    const [picked, setPicked] = useState({});         // { [f_id]: price string }
    const [search, setSearch] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open || !share) return;
        setStep(0); setPicked({}); setSearch(''); setError(''); setData(null);
        setLoading(true);
        businessAPI.getSharedProducts(businessId, share.f_id)
            .then((res) => setData(res.data))
            .catch((e) => setError(e.response?.data?.detail || 'Could not open the shared products.'))
            .finally(() => setLoading(false));
    }, [open, share, businessId]);

    const products = useMemo(() => data?.products || [], [data]);
    const q = search.trim().toLowerCase();
    const visible = products.filter((p) => !q || p.f_name.toLowerCase().includes(q)
        || (p.f_categories || []).some((c) => c.toLowerCase().includes(q)));
    const addable = visible.filter((p) => !p.already_added);
    const pickedIds = Object.keys(picked).map(Number);
    const allPicked = addable.length > 0 && addable.every((p) => picked[p.f_id] !== undefined);

    const toggle = (p) => setPicked((prev) => {
        const next = { ...prev };
        if (next[p.f_id] !== undefined) delete next[p.f_id];
        else next[p.f_id] = p.price != null ? String(p.price) : '';
        return next;
    });
    const toggleAll = () => setPicked((prev) => {
        const next = { ...prev };
        if (allPicked) addable.forEach((p) => { delete next[p.f_id]; });
        else addable.forEach((p) => { if (next[p.f_id] === undefined) next[p.f_id] = p.price != null ? String(p.price) : ''; });
        return next;
    });

    const chosen = products.filter((p) => picked[p.f_id] !== undefined);
    const missingPrice = chosen.some((p) => !(parseFloat(picked[p.f_id]) > 0));

    const add = async () => {
        setSaving(true); setError('');
        try {
            const res = await businessAPI.importSharedProducts(businessId, share.f_id, chosen.map((p) => ({
                product_id: p.f_id,
                price: parseFloat(picked[p.f_id]),
                day_prices: p.day_prices,
            })));
            onAdded && onAdded(res.data);
            onClose();
        } catch (e) {
            setError(e.response?.data?.detail || 'Could not add the products. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const from = data?.from_business_name || 'Shared products';

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} {...APP_DIALOG}>
            <DialogTitle sx={{ ...APP_DIALOG_TITLE_SX, display: 'flex', alignItems: 'center', gap: 1 }}>
                {step === 1 && (
                    <IconButton onClick={() => setStep(0)} sx={{ color: 'white', ml: -1 }}><ArrowBack /></IconButton>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="h6" noWrap>{step === 0 ? `Products from ${from}` : 'Check the prices'}</Typography>
                    <Typography variant="caption" sx={{ opacity: 0.85 }}>
                        {step === 0 ? 'Tick the ones you sell' : 'These are their prices — change any to yours'}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} disabled={saving} sx={{ color: 'white' }}><Close /></IconButton>
            </DialogTitle>

            <DialogContent dividers sx={{ p: 0 }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
                ) : error && !data ? (
                    <Alert severity="warning" sx={{ m: 2 }}>{error}</Alert>
                ) : step === 0 ? (
                    <>
                        <Box sx={{ px: 2, pt: 1.5, pb: 1, position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper',
                                   borderBottom: '1px solid', borderColor: 'divider' }}>
                            {products.length > 8 && (
                                <TextField fullWidth size="small" placeholder="Search products…" value={search}
                                    onChange={(e) => setSearch(e.target.value)} sx={{ mb: 1 }}
                                    InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
                            )}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                    {products.length} products · {pickedIds.length} ticked
                                </Typography>
                                {addable.length > 0 && (
                                    <Button size="small" onClick={toggleAll} sx={{ textTransform: 'none', fontWeight: 700 }}>
                                        {allPicked ? 'Untick all' : 'Tick all'}
                                    </Button>
                                )}
                            </Box>
                        </Box>
                        {visible.map((p) => {
                            const on = picked[p.f_id] !== undefined;
                            const days = p.schedule_label && p.schedule_label !== 'Every day' ? p.schedule_label : null;
                            const meta = [
                                p.price != null ? `₹${p.price} ${unitOf(p)}` : 'No price',
                                days,
                                p.plans?.length ? `+${p.plans.length} plan${p.plans.length > 1 ? 's' : ''}` : null,
                            ].filter(Boolean).join(' · ');
                            return (
                                <Box key={p.f_id} onClick={() => !p.already_added && toggle(p)}
                                    sx={{
                                        display: 'flex', alignItems: 'center', gap: 1.25, px: 2, py: 1, minHeight: 60,
                                        borderBottom: '1px solid', borderColor: 'divider', userSelect: 'none',
                                        cursor: p.already_added ? 'default' : 'pointer',
                                        bgcolor: on ? 'rgba(25,118,210,0.06)' : 'background.paper',
                                        opacity: p.already_added ? 0.55 : 1,
                                    }}>
                                    {p.already_added ? <CheckCircle sx={{ color: 'text.disabled' }} />
                                        : on ? <CheckCircle color="primary" /> : <RadioButtonUnchecked sx={{ color: 'text.disabled' }} />}
                                    {p.f_image_url
                                        ? <Box component="img" src={p.f_image_url} alt="" sx={{ width: 44, height: 44, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }} />
                                        : <Box sx={{ width: 44, height: 44, borderRadius: 1, bgcolor: 'grey.100', flexShrink: 0,
                                                     display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Inventory sx={{ color: 'text.disabled', fontSize: 22 }} />
                                          </Box>}
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight={on ? 800 : 600} noWrap>{p.f_name}</Typography>
                                        <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{meta}</Typography>
                                    </Box>
                                    {p.already_added && <Chip size="small" label="Already added" sx={{ flexShrink: 0 }} />}
                                </Box>
                            );
                        })}
                        {!visible.length && (
                            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 5 }}>No matches</Typography>
                        )}
                    </>
                ) : (
                    <Box sx={{ px: 2, py: 1 }}>
                        {chosen.map((p) => (
                            <Box key={p.f_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1,
                                                    borderBottom: '1px solid', borderColor: 'divider' }}>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography variant="body2" fontWeight={700} noWrap>{p.f_name}</Typography>
                                    {Object.keys(p.day_prices || {}).length > 0 && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            Day prices: {dayPricesText(p.day_prices)}
                                        </Typography>
                                    )}
                                </Box>
                                <TextField size="small" type="number" value={picked[p.f_id]}
                                    onChange={(e) => setPicked((prev) => ({ ...prev, [p.f_id]: e.target.value }))}
                                    error={!(parseFloat(picked[p.f_id]) > 0)}
                                    InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                                    inputProps={{ min: 0, step: 0.5, inputMode: 'decimal', 'aria-label': `${p.f_name} price` }}
                                    sx={{ width: 120, '& .MuiInputBase-root': { minHeight: 44 } }} />
                            </Box>
                        ))}
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                            Day prices come along as they are. You can change any price later from the product's Price button.
                        </Typography>
                    </Box>
                )}
                {error && data && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.25, gap: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                {step === 0 ? (
                    <Button fullWidth variant="contained" disableElevation disabled={!pickedIds.length}
                        onClick={() => setStep(1)}
                        sx={{ minHeight: 48, textTransform: 'none', fontWeight: 800 }}>
                        {pickedIds.length ? `Next — ${pickedIds.length} product${pickedIds.length > 1 ? 's' : ''}` : 'Tick the products you sell'}
                    </Button>
                ) : (
                    <Button fullWidth variant="contained" disableElevation disabled={saving || missingPrice}
                        onClick={add} startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
                        sx={{ minHeight: 48, textTransform: 'none', fontWeight: 800 }}>
                        {saving ? 'Adding…' : missingPrice ? 'Every product needs a price'
                            : `Add ${chosen.length} product${chosen.length > 1 ? 's' : ''}`}
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default SharedProductsDialog;
